import React from 'react';
import { ArrowLeftRight, Bitcoin, Bookmark, Eye, Flag, ImageOff, Sparkles } from 'lucide-react';
import BTCConverter from '../../BTCConverter';
import '../../../styles/components/ItemsList.css';
import { StatusMessage } from '../../ui/StatusMessage';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../config/featureFlags';

const getTitle = (asset) => asset.productName || asset.assetName || 'Marketplace listing';

const getDescription = (asset) =>
    asset.productDescription || asset.assetDescription || 'No description provided yet.';

const normalizeUserId = (value) => String(value || '').replace(/[|-]/g, '').trim();

const ItemsList = ({
    assets = [],
    loading = false,
    error = null,
    onRetry,
    onAssetImageClick,
    totalCount = assets.length,
    query = '',
    savedAssetIds = new Set(),
    savingAssetId = null,
    onToggleSaved,
    sellerReviewSummaries = {},
    onCreateListing,
    onReportListing,
}) => {
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
    const barterEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceBarter);
    const sheafEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase);
    if (loading) {
        return (
            <StatusMessage
                tone="loading"
                title="Loading marketplace listings"
                message="We are loading the latest buy, sell, and barter listings."
            />
        );
    }

    if (error) {
        return (
            <StatusMessage
                tone="error"
                title="Could not load marketplace"
                message={error}
                actionLabel={onRetry ? 'Retry' : undefined}
                onAction={onRetry}
            />
        );
    }

    if (assets.length === 0) {
        const isFiltered = totalCount > 0;
        return (
            <StatusMessage
                tone="empty"
                title={isFiltered ? 'No matching listings' : 'No marketplace listings'}
                message={isFiltered
                    ? `No active listings match "${query || 'your filters'}". Try a broader search or another payment type.`
                    : 'There are no active marketplace listings available right now.'}
                actionLabel={onCreateListing ? 'Give away or trade an item' : undefined}
                onAction={onCreateListing}
            />
        );
    }

    return (
        <div className="items-container">
            {assets.map((asset) => (
                <article key={asset.id} className="marketplace-listing-card">
                    {(() => {
                        const sellerSummary = sellerReviewSummaries[normalizeUserId(asset.ownedByUserId || asset.ownerId)];
                        return sellerSummary?.reviewCount ? (
                            <span className="marketplace-card-trust">
                                {Number(sellerSummary.averageRating || 0).toFixed(1)} / 5 seller
                            </span>
                        ) : null;
                    })()}
                    {onToggleSaved && (
                        <button
                            type="button"
                            className={savedAssetIds.has(Number(asset.id)) ? 'marketplace-save-button is-saved' : 'marketplace-save-button'}
                            onClick={() => onToggleSaved(asset.id)}
                            disabled={savingAssetId === Number(asset.id)}
                            aria-pressed={savedAssetIds.has(Number(asset.id))}
                            aria-label={`${savedAssetIds.has(Number(asset.id)) ? 'Remove saved listing' : 'Save listing'}: ${getTitle(asset)}`}
                        >
                            <Bookmark className="h-4 w-4" />
                            <span>{savedAssetIds.has(Number(asset.id)) ? 'Saved' : 'Save'}</span>
                        </button>
                    )}
                    <button
                        type="button"
                        className="marketplace-listing-media"
                        onClick={() => onAssetImageClick(asset)}
                        aria-label={`View ${getTitle(asset)}`}
                    >
                        {asset.imageURL ? (
                            <img src={asset.imageURL} alt={getTitle(asset)} />
                        ) : (
                            <span className="marketplace-listing-placeholder">
                                <ImageOff className="h-7 w-7" />
                            </span>
                        )}
                        <span className="marketplace-listing-view">
                            <Eye className="h-4 w-4" />
                            View
                        </span>
                    </button>

                    <div className="marketplace-listing-body">
                        <div className="marketplace-listing-badges">
                            {asset.isFree && <span>Free</span>}
                            {bitcoinEnabled && asset.ownedByGroupId && asset.forSale && <span><Bitcoin className="h-3.5 w-3.5" /> BTC</span>}
                            {barterEnabled && asset.tradable && <span><ArrowLeftRight className="h-3.5 w-3.5" /> Trade</span>}
                            {sheafEnabled && asset.ownedByGroupId && asset.acceptsSheaf && <span><Sparkles className="h-3.5 w-3.5" /> Sheaf</span>}
                        </div>
                        <h2>{getTitle(asset)}</h2>
                        <p>{getDescription(asset)}</p>

                        <div className="marketplace-listing-terms">
                            {bitcoinEnabled && asset.ownedByGroupId && asset.forSale && (
                                <div>
                                    <span>BTC price</span>
                                    <strong>
                                        <BTCConverter
                                            value={asset.salePrice}
                                            fontType="inherit"
                                            fontSize="15px"
                                            height="24px"
                                        />
                                    </strong>
                                </div>
                            )}
                            {sheafEnabled && asset.ownedByGroupId && asset.acceptsSheaf && (
                                <div>
                                    <span>Sheaf</span>
                                    <strong>{asset.sheafPrice}</strong>
                                </div>
                            )}
                            {barterEnabled && asset.tradable && (
                                <div>
                                    <span>Trade</span>
                                    <strong>Open</strong>
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            className="marketplace-listing-action"
                            onClick={() => onAssetImageClick(asset)}
                        >
                            Review listing
                        </button>
                        {onReportListing && (
                            <button
                                type="button"
                                className="marketplace-report-link"
                                onClick={() => onReportListing(asset)}
                                aria-label={`Report a concern about ${getTitle(asset)}`}
                            >
                                <Flag className="h-3.5 w-3.5" />
                                Report concern
                            </button>
                        )}
                    </div>
                </article>
            ))}
        </div>
    );
};

export default ItemsList;
